import _ from 'lodash';
import { getApiMethod } from '@/modules/apiSelector';
import { toggleBodyClass } from '@/utils/common';
import logger from '@/modules/Logger';

export default {
  namespaced: true,
  state: {
    noOpCommit: null,
    lessonId: null,
    hasMessages: false,
    messageEdits: [],
    lessonDetails: null,
    messages: null,
    events: null,
    accountType: null,
    chatThemeDark: false,
    isAllowedManageActions: false,
    isDownloadDisabled: false,
    currentChatHistoryId: null,
    currentChatEditId: null,
    currentChatEditMessage: null,
    serviceId: null
  },
  getters: {
    isTrainer: state => {
      return state.accountType === 't';
    },
    isStudent: state => {
      return state.accountType === 's';
    },
    isAdmin: state => {
      return state.accountType === 'a';
    },
    remoteUserNickname: (state, getters) => {
      return getters.isTrainer ? state.lessonDetails?.student_nickname : state.lessonDetails?.teacher_nickname;
    }
  },
  mutations: {
    setCurrentChatHistoryId(state, payload) {
      state.currentChatHistoryId = payload;
    },
    setCurrentChatHistoryMeta(state, payload) {
      state.currentChatEditId = payload?.messageId;
      state.currentChatEditMessage = payload?.message;
    },
    setCurrentChatEditMessage(state, payload) {
      state.currentChatEditMessage = payload;
    },
    noOpCommit(state, payload) {
      state.noOpCommit = payload;
    },
    setMessages(state, payload) {
      state.messages = payload;
    },
    setEvents(state, payload) {
      state.events = payload;
    },
    setChatHistoryInfo(state, payload) {
      state.lessonDetails = payload?.lesson_details;
      state.messages = payload?.messages;
      state.events = payload?.events;
      state.accountType = payload?.account_type;
      state.lessonId = parseInt(payload?.student_book_id);
      state.serviceId = payload?.service_id;

      if (state.messages?.length > 0) {
        state.hasMessages = true;
      } else {
        state.hasMessages = false;
      }

      state.isDownloadDisabled = !state.hasMessages
      // `isAllowedManageActions` - for now, this is only ALLOWED for ADMIN and STUDENT
      // based on condition from [bizmates.jp]/fuel/app/views/pc/mystage/header.twig
      // but also [bizmates-trainer-frontend] doesnt have a download feature enabled
      state.isAllowedManageActions = state.accountType != 't';
    },
    setChatEditsHistoryInfo(state, payload) {
      state.messageEdits[payload?.message_id] = payload;
    },
    deleteChatMessageInfo(state, payload) {
      const wasAffected = payload?.rows > 0;
      if (!wasAffected) {
        console.log('Deletion not successful.');
        return;
      }
      const messageId = payload?.id;
      state.messageEdits[messageId] = payload;
      let messageEdits = [];
      state.messageEdits.forEach((elm, ind) => {
        if (ind != payload.id) {
          messageEdits[ind] = elm;
        }
      });
      state.messageEdits = messageEdits;

      const index = _.findIndex(state.messages, { message_id: messageId });

      if (index !== -1) {
        state.messages[index].deleted = true;
      }
    },
    updateChatMessageInfo(state, payload) {
      const wasAffected = payload?.rows > 0;
      if (!wasAffected) {
        console.log('Update not successful.');
        return;
      }
      const messageId = payload?.id;
      const message = payload?.message;
      
      const index = _.findIndex(state.messages, { message_id: messageId });

      if (index !== -1) {
        let targetMessage = state.messages[index];
        targetMessage.message = message;
        targetMessage.editted = true;
        state.messages[index] = targetMessage;
      }
    }
  },
  actions: {
    /**
       * Get lesson chat history
       *
       * @param {*} state
       * @param {*} token
       */
    async getChatHistory({ commit }, params) {
      const apiMethod = getApiMethod('getChatHistory');
      let response = null;
      try {
        const { result, data, errMessage } = await apiMethod(params, false, (data) => {
          logger.info('Loading chat history', {
            operation: 'chat-history:module-load',
            payload: params
          });

          commit('setChatHistoryInfo', data);
        });

        response = result ? data : errMessage;
      } catch (error) {
        logger.error('Failed to retrieve chat history', error);
        response = error.message;
      }

      return response;
    },
    /**
     * Download chat messages as CSV
     */
    downloadMessages({ state, commit })
    {
      let csvData = [
        ['Date', 'Message', 'Sender']
      ];
      state.messages.forEach((elm) => {
        let tmpDiv = document.createElement("div");
        tmpDiv.innerHTML = elm.message;
        csvData.push([
          elm.dateTime.date+' '+elm.dateTime.localeTime,
          tmpDiv.textContent || tmpDiv.innerText || "",
          elm.from == 's' ? state.lessonDetails.student_nickname : state.lessonDetails.teacher_nickname,
        ]);
      });

      let csvString = '';
      csvData.forEach(row => {
        csvString += row.map(value => {
          // Escape double quotes and enclose if necessary
          const escapedValue = String(value).replace(/"/g, '""');
          return (escapedValue.includes(',') || escapedValue.includes('\n')) ? `"${escapedValue}"` : escapedValue;
        }).join(',') + '\n';
      });

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const filename = 'ChatHistory'+state.lessonId+'.csv';

      if (navigator.msSaveBlob) { // IE 10+
        navigator.msSaveBlob(blob, filename);
      } else {
        let link = document.createElement('a');
        let url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      commit('noOpCommit', 1);
    },
    /**
     * Toggle chat history page theme, from `dark` or `white` (default)
     * 
     * @param {*} color 
     */
    toggleTheme({ state, commit }, color) {
      state.chatThemeDark = (color === null ? !state.chatThemeDark : color === 'dark');
      toggleBodyClass(state.chatThemeDark);

      commit('noOpCommit', 1);
    }
  }
};
